"use client";

import Link from "next/link";
import Image from "next/image";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { MapPinIcon, HeartIcon, NavigationIcon } from "lucide-react";

import { CategoryBadge } from "@/components/events/CategoryBadge";
import { Card, CardContent, CardHeader, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { EVENT_TYPES, EVENT_CATEGORIES, formatPrice, type EventCategoryKey, type EventTypeKey } from "@/lib/constants";
import { toDisplayDate } from "@/lib/eventDate";
import { useFavorites } from "@/lib/favorites";
import { cn } from "@/lib/utils";

export type PublicEvent = {
  id: number;
  title: string;
  eventType: EventTypeKey;
  category: EventCategoryKey;
  startsAt: string;
  venue: string;
  city: string;
  price: string | null;
  imageUrl: string | null;
  organizationId: number;
  organizationName: string;
  latitude: number | null;
  longitude: number | null;
};

export function EventCard({
  event,
  distanceKm,
}: {
  event: PublicEvent;
  distanceKm?: number | null;
}) {
  const date = toDisplayDate(new Date(event.startsAt));
  const { isFavorite, toggle } = useFavorites();
  const favorite = isFavorite(event.id);
  const dateCode = EVENT_CATEGORIES[event.category].label.slice(0, 3).toUpperCase();

  return (
    <Card className="h-full overflow-hidden transition-shadow hover:shadow-md">
      {event.imageUrl && (
        <Link href={`/eventos/${event.id}`} className="-mt-4 mb-1 block">
          <Image
            src={event.imageUrl}
            alt=""
            width={400}
            height={220}
            className="h-40 w-full object-cover"
          />
        </Link>
      )}
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <span className="font-mono text-xs font-semibold tracking-wide text-muted-foreground uppercase">
            {dateCode} · {format(date, "dd.MM")}
          </span>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              toggle(event.id);
            }}
            aria-label={favorite ? "Quitar de favoritos" : "Añadir a favoritos"}
            className="-mt-1 -mr-1 shrink-0 rounded-full p-1.5 text-muted-foreground hover:bg-muted hover:text-destructive"
          >
            <HeartIcon className={cn("size-4", favorite && "fill-destructive text-destructive")} />
          </button>
        </div>

        <Link href={`/eventos/${event.id}`} className="block">
          <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
            <CategoryBadge category={event.category} />
            <Badge variant="outline">{EVENT_TYPES[event.eventType]}</Badge>
          </div>
          <h3 className="font-heading text-lg leading-snug font-semibold hover:underline">
            {event.title}
          </h3>
        </Link>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        <span className="text-xs font-medium text-muted-foreground">
          Organiza: {event.organizationName}
        </span>
        <div className="flex flex-col gap-1 text-sm text-foreground/80">
          <span>{format(date, "EEEE d 'de' MMMM, HH:mm'h'", { locale: es })}</span>
          <span className="flex items-center gap-1.5">
            <MapPinIcon className="size-3.5 text-primary" />
            {event.venue}
            {typeof distanceKm === "number" && (
              <span className="ml-1 inline-flex items-center gap-1 text-xs text-muted-foreground">
                <NavigationIcon className="size-3" />a{" "}
                {distanceKm < 1 ? `${Math.round(distanceKm * 1000)} m` : `${distanceKm.toFixed(1)} km`}
              </span>
            )}
          </span>
        </div>
      </CardContent>
      <CardFooter className="mt-auto justify-between">
        <span className="text-xs text-muted-foreground">{event.city}</span>
        <span className="text-sm font-semibold text-primary">{formatPrice(event.price) ?? "—"}</span>
      </CardFooter>
    </Card>
  );
}
